import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import httpClient from "../../../lib/httpClient";
import { Grid } from "@material-ui/core";
import {
  GridFilterModel,
  GridValueFormatterParams,
  GridPaginationModel,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import {
  tableCellRender,
  stringFilterOperators,
  getLatestUpdateDaily,
  numberFilterOperators,
  fixedFormatter,
} from "../../util/tableUtils";
import { initialFilterModel, isThereAnyIncompleteFilter } from "../../util/tableUtilsTS";
import { DataGridCystellarTable } from "../../util/DataGridCystellarTable";
import { apiError } from "../../../lib/apiError";

import { getUserPermissionsList } from "../../util/permissionsUtils";
import { DownloadTableButton } from "../../util/DownloadTableButton";
import { getFilterQuery, getSortDetailsString } from "../../emergencyMonitoring/emergencyUtils";
import { PermissionIdEnum } from "../../util/types/permissions/permissionIdEnum";

interface EmergencyMunicipalitiesProps {
  activationCode: string;
}

interface RowData {
  _id: string;
  uniqueIndex: number;
  // Other fields are not required by typecheck.
}

export const EmergencyMunicipalities: React.FC<EmergencyMunicipalitiesProps> = ({ activationCode }) => {
  const { t } = useTranslation();
  const [municipalitiesData, setMunicipalitiesData] = useState([]);
  const [pinnedSumRowV3, setPinnedSumRowV3] = useState<any>({});

  const [tableState, setTableState] = useState({
    isLoading: false,
    total: 0,
  });

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [filterModel, setFilterModel] = useState<GridFilterModel>(initialFilterModel);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  const [lastUpdateDatePoliciesMsg, setLastUpdateDatePoliciesMsg] = useState("");

  const onFilterChange = useCallback((aFilterModel) => {
    setFilterModel(aFilterModel);
  }, []);

  const onSortChange = useCallback((aSortModel: GridSortModel) => {
    setSortModel(aSortModel);
  }, []);

  useEffect(() => {
    getLatestUpdateDaily("/serviceLogs/buildings?service=db_buildings_unique").then((lastUpdateMsg) => {
      setLastUpdateDatePoliciesMsg(lastUpdateMsg);
    });
  }, []);

  const getMunicipalitiesData = useCallback(async () => {
    setTableState((old) => ({ ...old, isLoading: true }));

    try {
      const { data: municipalitiesResponse } = await httpClient().get(
        `/terrashield/terraShieldComunes?page=${paginationModel.page}&pageSize=${paginationModel.pageSize}&filters=${getFilterQuery(filterModel, activationCode)}&${getSortDetailsString(sortModel)}`,
      );
      console.log("municipalitiesResponse Sum", municipalitiesResponse.totalSum);

      if (municipalitiesResponse.total > 0) {
        setPinnedSumRowV3(getPinnedSunRowV3(municipalitiesResponse.totalSum));
      } else {
        // Aggregator returns empty object
        setPinnedSumRowV3({});
      }
      setMunicipalitiesData(municipalitiesResponse.data);

      setTableState((old) => ({
        ...old,
        isLoading: false,
        total: municipalitiesResponse.total,
      }));
    } catch (error) {
      apiError(error);

      setMunicipalitiesData([]);
      setTableState((old) => ({
        ...old,
        isLoading: false,
        total: 0,
      }));
    }
  }, [filterModel, paginationModel, sortModel, activationCode]);

  useEffect(() => {
    if (!isThereAnyIncompleteFilter(filterModel)) {
      getMunicipalitiesData();
    }
  }, [filterModel, getMunicipalitiesData]);

  const downloadEmergencyComuniTableFunction = async () => {
    try {
      const { data: claimsResponse } = await httpClient().get(
        `/terrashield/terraShieldComunes?page=0&pageSize=9999999&filters=${getFilterQuery(filterModel, activationCode)}&${getSortDetailsString(sortModel)}`,
      );

      return claimsResponse.data;
    } catch (error) {
      apiError(error);
      return [];
    }
  };

  const getPinnedSunRowV3 = (totalSum: any) => {
    console.log("totalSum", totalSum);
    return {
      _id: "total-row",
      addressCityCode: "TOTAL",
      policies: totalSum.totalPolicies,
      policiesTIV: totalSum.totalPoliciesTIV,
      flood: totalSum.totalFlood,
      floodTIV: totalSum.totalFloodTIV,
      claims: totalSum.totalClaims,
      claimsTIV: totalSum.totalClaimsTIV,
      floodClaims: totalSum.totalFloodClaims,
      floodClaimsTIV: totalSum.totalFloodClaimsTIV,
    };
  };

  const municipalitiesColumns = [
    {
      field: "addressCityCode",
      headerName: t("cityCode"),
      width: 150,
    },
    {
      field: "addressCity",
      headerName: t("city"),
      width: 150,
    },
    { field: "addressProvince", headerName: t("state"), width: 80 },
    {
      field: "policies",
      headerName: "Policies in AOI",
      width: 110,
    },
    {
      field: "policiesTIV",
      headerName: "TIV in AOI",
      width: 120,
      filterOperators: numberFilterOperators,
      align: "right",
      ...fixedFormatter,
    },
    {
      field: "flood",
      headerName: "Policies in Obs Area",
      width: 150,
    },

    {
      field: "floodTIV",
      headerName: "TIV in Obs Area",
      width: 120,
      filterOperators: numberFilterOperators,
      align: "right",
      ...fixedFormatter,
    },
    {
      field: "claims",
      headerName: "Claims",
      width: 70,
    },

    {
      field: "claimsTIV",
      headerName: "Claims TIV",
      width: 100,
      filterOperators: numberFilterOperators,
      align: "right",
      ...fixedFormatter,
    },

    {
      field: "floodClaims",
      headerName: "Flood Claims",
      width: 100,
    },

    {
      field: "floodClaimsTIV",
      headerName: "Flood Claims TIV",
      width: 130,
      filterOperators: numberFilterOperators,
      align: "right",
      ...fixedFormatter,
    },
  ];

  const columns = municipalitiesColumns.map((tableColumn) => ({
    valueFormatter: (params: GridValueFormatterParams) => tableCellRender(params.value?.toString()),
    filterOperators: stringFilterOperators,

    ...tableColumn,
  }));

  return (
    <Grid item xs={12} sm={12}>
      <DataGridCystellarTable
        columns={columns}
        rows={municipalitiesData}
        rowCount={tableState.total}
        getRowId={(rowData: RowData) => rowData._id}
        loading={tableState.isLoading}
        paginationMode={"server"}
        paginationModel={paginationModel}
        pageSizeOptions={[10, 20, 50]}
        onPaginationModelChange={(newPaginationModel: GridPaginationModel) =>
          setPaginationModel(newPaginationModel)
        }
        filterMode={"server"}
        onFilterModelChange={onFilterChange}
        sortingMode={"server"}
        onSortModelChange={onSortChange}
        customToolbarComp={true}
        customToolbarCompMsg={lastUpdateDatePoliciesMsg}
        pinnedRows={municipalitiesData.length ? { top: [pinnedSumRowV3] } : {}}
        customToolbarWidgets={
          <>
            {getUserPermissionsList().includes(PermissionIdEnum.Super) && (
              <DownloadTableButton
                downloadTableFunction={downloadEmergencyComuniTableFunction}
                total={tableState.total}
                fileName="ComuneEmergenza"
              ></DownloadTableButton>
            )}
          </>
        }
      />
    </Grid>
  );
};
